// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useExerciseSubmission } from '@hooks/mutations/ledger/useSubmission';
import { Credential } from '@daml.js/dtc-credential/lib/Dtc/Credential/V3/Credential';
import {
  RegistrarService,
  RegistrarService_FreezePositions,
} from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/Registrar';
import { Holding } from '@daml.js/dtc-registry-holding/lib/Dtc/Registry/Holding/V3/Holding';
import { InstrumentConfiguration } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Configuration/Instrument';
import { DenyList } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Rbac/Deny';
import { ContractId } from '@daml/types';
import { useLoggedInUser } from '../../../../contexts/UserContext';
import { ERR_FREEZE, SUC_FREEZE } from '../../../../utils/strings';

interface Args {
  registrarServiceCid: string;
  credentialCids: string[];
  denyListCids: string[];
  holdingCids: string[];
  freezeId: string;
  reason: string;
  instrumentConfigurationCid: string;
}

export const useRequestFreeze = () => {
  const { submit: freezePositions } = useExerciseSubmission(
    RegistrarService.RegistrarService_FreezePositions,
    SUC_FREEZE,
    ERR_FREEZE,
  );

  const { userId } = useLoggedInUser();

  return async (args: Args) => {
    const {
      registrarServiceCid,
      credentialCids,
      denyListCids,
      holdingCids,
      freezeId,
      reason,
      instrumentConfigurationCid,
    } = args;

    const argument: RegistrarService_FreezePositions = {
      credentialCids: credentialCids
        ? credentialCids.map((cid) => cid as ContractId<Credential>)
        : [],
      denyListCids: denyListCids ? denyListCids.map((cid) => cid as ContractId<DenyList>) : [],
      holdingCids: holdingCids ? holdingCids.map((cid) => cid as ContractId<Holding>) : [],
      userId,
      freezeId,
      reason,
      instrumentConfigurationCid: instrumentConfigurationCid as ContractId<InstrumentConfiguration>,
    };

    await freezePositions({
      cid: registrarServiceCid as ContractId<RegistrarService>,
      argument,
    });
  };
};
